import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Trophy, Gift, Calendar, User, Home, Tractor, Bike, Shield, Award, CheckCircle, Globe, Laptop, Tv } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { API_URL as API } from '../config/api';

const Winners = () => {
  const { t, i18n } = useTranslation();
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const locale = i18n.language === 'fr' ? fr : enUS;

  useEffect(() => {
    const fetchWinners = async () => {
      try {
        const response = await axios.get(`${API}/winners`);
        setWinners(response.data);
      } catch (error) {
        console.error('Failed to fetch winners:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWinners();
  }, []);

  const prizeInfo = {
    house: {
      title: i18n.language === 'fr' ? 'Maison Autosuffisante' : i18n.language === 'ht' ? 'Kay Otonòm' : 'Self-Sufficient House',
      value: '450 000 USD',
      icon: Home,
      color: 'from-secondary to-accent',
      bgColor: 'bg-secondary',
      order: 1
    },
    tractor: {
      title: i18n.language === 'fr' ? 'Tracteur Agricole' : i18n.language === 'ht' ? 'Traktè Agrikòl' : 'Agricultural Tractor',
      value: '40 000 USD',
      icon: Tractor,
      color: 'from-accent to-primary',
      bgColor: 'bg-accent',
      order: 2
    },
    moto: {
      title: i18n.language === 'fr' ? 'Grand Tricycle Cargo' : i18n.language === 'ht' ? 'Gran Trisikl Kago' : 'Large Cargo Tricycle',
      value: '5 000 USD',
      icon: Bike,
      color: 'from-primary to-secondary',
      bgColor: 'bg-primary',
      order: 3
    },
    laptop: {
      title: i18n.language === 'fr' ? 'Super Laptop HP Envy' : i18n.language === 'ht' ? 'Sipè Laptop HP Envy' : 'Super HP Envy Laptop',
      value: '3 000 USD',
      icon: Laptop,
      color: 'from-blue-600 to-blue-400',
      bgColor: 'bg-blue-600',
      order: 4
    },
    tv: {
      title: i18n.language === 'fr' ? 'TV LG OLED 65"' : i18n.language === 'ht' ? 'TV LG OLED 65"' : 'LG OLED 65" TV',
      value: '2 000 USD',
      icon: Tv,
      color: 'from-purple-600 to-purple-400',
      bgColor: 'bg-purple-600',
      order: 5
    }
  };

  const texts = {
    fr: {
      title: 'Les Gagnants',
      subtitle: "Découvrez les heureux gagnants du Marathon de l'Avenir EDENLIFE 2026",
      noWinners: 'Aucun gagnant pour le moment',
      noWinnersDesc: 'Le tirage au sort aura lieu en décembre 2026. Participez dès maintenant pour avoir votre chance de gagner !',
      winningTicket: 'Billet Gagnant',
      drawDate: 'Date du tirage',
      winner: 'GAGNANT',
      prizeClaimed: 'Prix réclamé',
      pending: 'En attente',
      prizesTitle: 'Les 5 Prix à Gagner',
      trustTitle: 'Fiabilité & Sécurité',
      trustDesc: "Marathon de l'Avenir EDENLIFE International s'engage à garantir la transparence, la sécurité et la fiabilité de toutes nos opérations.",
      trustedPartners: 'Partenaires de confiance',
      secureTransactions: 'Transactions sécurisées',
      verifiedOrg: 'Organisation vérifiée',
      compliantPayments: 'Paiements conformes',
      intlPresence: 'Présence internationale'
    },
    en: {
      title: 'Winners',
      subtitle: 'Discover the lucky winners of the EDENLIFE Marathon 2026',
      noWinners: 'No winners yet',
      noWinnersDesc: 'The draw will take place in December 2026. Participate now for your chance to win!',
      winningTicket: 'Winning Ticket',
      drawDate: 'Draw Date',
      winner: 'WINNER',
      prizeClaimed: 'Prize claimed',
      pending: 'Pending',
      prizesTitle: '5 Prizes to Win',
      trustTitle: 'Trust & Security',
      trustDesc: 'EDENLIFE International Marathon is committed to ensuring transparency, security and reliability of all our operations.',
      trustedPartners: 'Trusted Partners',
      secureTransactions: 'Secure transactions',
      verifiedOrg: 'Verified organization',
      compliantPayments: 'Compliant payments',
      intlPresence: 'International presence'
    },
    ht: {
      title: 'Gayan yo',
      subtitle: 'Dekouvri gayan Maraton EDENLIFE Entènasyonal 2026 yo',
      noWinners: 'Poko gen gayan',
      noWinnersDesc: 'Tiraj la ap fèt an desanm 2026. Patisipe kounye a pou gen chans ou genyen!',
      winningTicket: 'Tikè Gayan',
      drawDate: 'Dat Tiraj',
      winner: 'GAYAN',
      prizeClaimed: 'Pri reklame',
      pending: 'An atant',
      prizesTitle: '5 Pri pou Genyen',
      trustTitle: 'Konfyans & Sekirite',
      trustDesc: 'Maraton EDENLIFE Entènasyonal angaje li pou garanti transparans, sekirite ak fyabilite tout operasyon nou yo.',
      trustedPartners: 'Patnè Konfyans',
      secureTransactions: 'Tranzaksyon sekirize',
      verifiedOrg: 'Òganizasyon verifye',
      compliantPayments: 'Peman konfòm',
      intlPresence: 'Prezans entènasyonal'
    },
    es: {
      title: 'Ganadores 2026',
      subtitle: 'Descubre los afortunados ganadores de la Maratón EDENLIFE 2026',
      comingSoon: 'Próximo Sorteo',
      comingSoonDesc: 'Los ganadores serán anunciados después del sorteo de diciembre 2026',
      drawDate: 'Fecha del sorteo',
      grandPrize: 'Premio Mayor',
      secondPrize: 'Segundo Premio',
      thirdPrize: 'Tercer Premio',
      prize: 'Premio',
      winner: 'Ganador',
      ticketNumber: 'Número de boleto',
      location: 'País',
      prizeValue: 'Valor del premio',
      liveDrawTitle: 'Sorteo en Vivo',
      liveDrawDesc: 'Sigue el sorteo en directo en nuestras redes sociales',
      transparencyTitle: 'Transparencia Total',
      transparencyDesc: 'El sorteo se realiza en presencia de un notario y se transmite en vivo para garantizar una imparcialidad absoluta.',
      notaryVerified: 'Certificado notarial',
      liveBroadcast: 'Transmisión en vivo',
      publicAnnouncement: 'Anuncio público',
      participateBtn: 'Participar en la Maratón',
      previousWinners: 'Ganadores Anteriores',
      noPreviousWinners: '¡Esta es nuestra primera edición! Sé parte de la historia.',
      totalRaised: 'Total Recaudado',
      projectsFunded: 'Proyectos Financiados',
      livesImpacted: 'Vidas Impactadas',
      prizes: {
        first: 'Casa Autosuficiente - 450,000 USD',
        second: 'Tractor Agrícola - 40,000 USD',
        third: 'Triciclo de Carga - 5,000 USD',
        fourth: 'Super Laptop HP Envy - 3,000 USD',
        fifth: 'TV LG OLED 65" - 2,000 USD'
      },
      trustBadges: 'Socios de Confianza',
      securityNote: 'Todos los pagos son procesados de forma segura por nuestros socios certificados',
      trustedPartners: 'Socios de Confianza',
      secureTransactions: 'Transacciones seguras',
      verifiedOrg: 'Organización verificada',
      compliantPayments: 'Pagos conformes',
      intlPresence: 'Presencia internacional'
    },
    pt: {
      title: 'Ganhadores 2026',
      subtitle: 'Descubra os felizes ganhadores da Maratona EDENLIFE 2026',
      comingSoon: 'Próximo Sorteio',
      comingSoonDesc: 'Os ganhadores serão anunciados após o sorteio de dezembro 2026',
      drawDate: 'Data do sorteio',
      grandPrize: 'Grande Prêmio',
      secondPrize: 'Segundo Prêmio',
      thirdPrize: 'Terceiro Prêmio',
      prize: 'Prêmio',
      winner: 'Ganhador',
      ticketNumber: 'Número do bilhete',
      location: 'País',
      prizeValue: 'Valor do prêmio',
      liveDrawTitle: 'Sorteio ao Vivo',
      liveDrawDesc: 'Acompanhe o sorteio ao vivo em nossas redes sociais',
      transparencyTitle: 'Transparência Total',
      transparencyDesc: 'O sorteio é realizado na presença de um notário e transmitido ao vivo para garantir imparcialidade absoluta.',
      notaryVerified: 'Certificado notarial',
      liveBroadcast: 'Transmissão ao vivo',
      publicAnnouncement: 'Anúncio público',
      participateBtn: 'Participar da Maratona',
      previousWinners: 'Ganhadores Anteriores',
      noPreviousWinners: 'Esta é a nossa primeira edição! Faça parte da história.',
      totalRaised: 'Total Arrecadado',
      projectsFunded: 'Projetos Financiados',
      livesImpacted: 'Vidas Impactadas',
      prizes: {
        first: 'Casa Autossuficiente - 450.000 USD',
        second: 'Trator Agrícola - 40.000 USD',
        third: 'Triciclo de Carga - 5.000 USD',
        fourth: 'Super Laptop HP Envy - 3.000 USD',
        fifth: 'TV LG OLED 65" - 2.000 USD'
      },
      trustBadges: 'Parceiros de Confiança',
      securityNote: 'Todos os pagamentos são processados de forma segura por nossos parceiros certificados',
      trustedPartners: 'Parceiros de Confiança',
      secureTransactions: 'Transações seguras',
      verifiedOrg: 'Organização verificada',
      compliantPayments: 'Pagamentos conformes',
      intlPresence: 'Presença internacional'
    }
  };

  const txt = texts[i18n.language] || texts.fr;

  // Sort winners by prize order (1st, 2nd, 3rd)
  const sortedWinners = [...winners].sort((a, b) => {
    const orderA = prizeInfo[a.prize_type]?.order || 99;
    const orderB = prizeInfo[b.prize_type]?.order || 99;
    return orderA - orderB;
  });

  const certifications = [
    {
      name: 'SSL Secured',
      icon: Shield,
      desc: txt.secureTransactions
    },
    {
      name: 'Verified Nonprofit',
      icon: CheckCircle,
      desc: txt.verifiedOrg
    },
    {
      name: 'PCI Compliant',
      icon: Award,
      desc: txt.compliantPayments
    },
    {
      name: 'Global Reach',
      icon: Globe,
      desc: txt.intlPresence
    }
  ];

  return (
    <div className="min-h-screen pt-24 px-6 pb-12" style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-12">
            <Trophy className="w-20 h-20 text-secondary mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {txt.title}
            </h1>
            <p className="text-lg md:text-xl text-gray-600">
              {txt.subtitle}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary mx-auto"></div>
            </div>
          ) : winners.length === 0 ? (
            <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-12 text-center">
              <Gift className="w-24 h-24 text-gray-300 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-700 mb-4">
                {txt.noWinners}
              </h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {txt.noWinnersDesc}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {sortedWinners.map((winner, index) => {
                const prize = prizeInfo[winner.prize_type] || prizeInfo.house;
                const PrizeIcon = prize.icon;
                return (
                  <motion.div
                    key={winner.winner_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-12 overflow-hidden relative"
                  >
                    <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-br ${prize.color} opacity-10 rounded-full -mr-32 -mt-32`}></div>
                    
                    <div className="relative z-10">
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4 mb-4">
                            <div className={`w-16 h-16 ${prize.bgColor} rounded-2xl flex items-center justify-center`}>
                              <PrizeIcon className="w-8 h-8 text-white" />
                            </div>
                            <div>
                              <h3 className="text-2xl md:text-3xl font-bold text-primary" style={{ fontFamily: 'Playfair Display, serif' }}>
                                {prize.title}
                              </h3>
                              <p className="text-secondary font-bold text-xl">{prize.value}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            <div className="flex items-center space-x-3">
                              <User className="w-5 h-5 text-primary" />
                              <div>
                                <p className="text-sm text-gray-600">{txt.winningTicket}</p>
                                <p className="font-bold text-primary text-lg">{winner.ticket_number}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <Calendar className="w-5 h-5 text-primary" />
                              <div>
                                <p className="text-sm text-gray-600">{txt.drawDate}</p>
                                <p className="font-semibold">{format(new Date(winner.draw_date), 'PPP', { locale })}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-secondary to-accent rounded-2xl p-6 text-center min-w-[200px]">
                          <Trophy className="w-12 h-12 text-white mx-auto mb-2" />
                          <p className="text-white font-bold text-lg">
                            {txt.winner}
                          </p>
                          <p className="text-white/80 text-sm">
                            {winner.claimed ? txt.prizeClaimed : txt.pending}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Prize Information - All 5 Prizes */}
          <div className="mt-12 bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              {txt.prizesTitle}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(prizeInfo).map(([key, prize]) => {
                const PrizeIcon = prize.icon;
                return (
                  <motion.div 
                    key={key} 
                    whileHover={{ scale: 1.02 }}
                    className={`bg-gradient-to-br ${prize.color} rounded-2xl p-6 text-white text-center shadow-lg`}
                  >
                    <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                      <PrizeIcon className="w-10 h-10" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{prize.title}</h3>
                    <p className="text-2xl font-bold">{prize.value}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Trust & Certifications Section */}
          <div className="mt-12 bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8">
            <h2 className="text-xl md:text-2xl font-bold text-primary mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              {txt.trustTitle}
            </h2>
            <p className="text-gray-600 text-center mb-8 max-w-3xl mx-auto">
              {txt.trustDesc}
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {certifications.map((cert, index) => {
                const CertIcon = cert.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center p-4 rounded-xl hover:bg-primary/5 transition-colors"
                  >
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
                      <CertIcon className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="font-bold text-primary text-sm mb-1">{cert.name}</h4>
                    <p className="text-xs text-gray-500">{cert.desc}</p>
                  </motion.div>
                );
              })}
            </div>

            {/* Partners / Badges Row */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="text-center text-sm text-gray-500 mb-6">
                {txt.trustedPartners}
              </p>
              <div className="flex flex-wrap justify-center items-center gap-8">
                {/* Stripe Badge */}
                <div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-lg">
                  <svg className="w-12 h-6" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M59.64 14.28c0-4.52-2.19-8.1-6.38-8.1-4.21 0-6.76 3.58-6.76 8.07 0 5.33 3.01 8.02 7.33 8.02 2.11 0 3.7-.48 4.9-1.15v-3.53c-1.2.61-2.58.99-4.33.99-1.71 0-3.23-.6-3.42-2.69h8.62c0-.23.04-1.14.04-1.61zm-8.71-1.68c0-2 1.23-2.83 2.35-2.83 1.09 0 2.25.83 2.25 2.83h-4.6zM39.25 6.18c-1.73 0-2.84.81-3.46 1.38l-.23-1.1h-3.87v20.54l4.4-.93.01-4.99c.64.46 1.57 1.12 3.12 1.12 3.16 0 6.04-2.54 6.04-8.14-.01-5.12-2.93-7.88-6.01-7.88zm-1.06 12.11c-1.04 0-1.65-.37-2.07-.83l-.02-6.55c.46-.51 1.09-.87 2.09-.87 1.6 0 2.71 1.79 2.71 4.12 0 2.37-1.09 4.13-2.71 4.13zM26.11 5.17l4.42-.94V.76l-4.42.94v3.47zM26.11 6.51h4.42v15.28h-4.42V6.51zM21.21 7.74l-.28-1.23h-3.81v15.28h4.4V11.3c1.04-1.36 2.8-1.1 3.35-.91V6.51c-.57-.21-2.64-.61-3.66 1.23zM12.63 2.53L8.31 3.45l-.01 13.99c0 2.58 1.94 4.49 4.52 4.49 1.43 0 2.48-.26 3.05-.58v-3.57c-.56.22-3.32 1.02-3.32-1.54V10.1h3.32V6.51h-3.32l.08-3.98zM3.52 10.94c0-.69.57-.95 1.51-.95 1.35 0 3.06.41 4.41 1.14V7.05c-1.48-.58-2.94-.81-4.41-.81C1.98 6.24 0 7.85 0 10.51c0 4.14 5.7 3.48 5.7 5.26 0 .82-.71 1.08-1.7 1.08-1.47 0-3.35-.6-4.84-1.42v4.11c1.65.71 3.31 1.01 4.84 1.01 3.12 0 5.27-1.54 5.27-4.24-.01-4.47-5.75-3.67-5.75-5.37z" fill="#635BFF"/>
                  </svg>
                  <span className="text-xs text-gray-500">Payments</span>
                </div>

                {/* SSL Badge */}
                <div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-lg">
                  <Shield className="w-6 h-6 text-green-600" />
                  <span className="text-xs font-semibold text-gray-600">SSL 256-bit</span>
                </div>

                {/* PCI Badge */}
                <div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                  <span className="text-xs font-semibold text-gray-600">PCI DSS</span>
                </div>

                {/* Nonprofit Badge */}
                <div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-lg">
                  <Award className="w-6 h-6 text-amber-600" />
                  <span className="text-xs font-semibold text-gray-600">Nonprofit</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Winners;
